import { appKey } from '@app/shared/brand'
import { Link, useRouterState } from '@tanstack/react-router'
import {
  Building2,
  CalendarDays,
  Check,
  ChevronsUpDown,
  FileText,
  Images,
  KanbanSquare,
  LayoutDashboard,
  LayoutGrid,
  Library,
  type LucideIcon,
  Music,
  Network,
  Plug,
  Receipt,
  Send,
  Settings,
  Shapes,
  SlidersHorizontal,
  Sparkles,
  Type,
  UserRound,
  Users,
} from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { createPortal } from 'react-dom'
import { DocNav, FolderTreeNav } from '../../routes/studio/FolderNav'
import { Caret, initialsOf, SegmentedControl } from '../../ui'
import type { Surface } from '../../ui/UserMenu'
import { usePopover } from '../../ui/usePopover'
import { useAuth } from '../auth'
import { useSpace } from '../org'
import { type OrgScope, useOrgScopeControl } from '../org-scope'
import { siteCarriesTimes } from '../public-router'
import { DOC_PAGE_TITLES } from './doc-titles'
import { belongsAt, rosterIsDonors } from './products'
import { useFlyout } from './sidebar-flyout'

/** The people section's name: what it lists where this page is served. */
const rosterLabel = () => (rosterIsDonors() ? 'Donors' : 'Members')

/** Whether the sidebar is collapsed, remembered per browser. */
const SIDEBAR_KEY = appKey('sidebarCollapsed')

/**
 * The org identity + switcher at the top of the sidebar — a proper control, not
 * a bare label. With one org it names where you are; with several it opens a
 * menu to switch. The choice persists (see `useOrg`).
 */
function SidebarOrg() {
  const { spaces, spaceId, setSpaceId } = useSpace()
  const { open, setOpen, ref, panelRef } = usePopover()

  const current = spaces.find((s) => s.spaceId === spaceId) ?? spaces[0]
  if (!current) return null
  const multi = spaces.length > 1

  return (
    <div className="sb-org" ref={ref}>
      <button
        type="button"
        className="sb-org__trigger"
        onClick={() => multi && setOpen((o) => !o)}
        aria-haspopup={multi ? 'menu' : undefined}
        aria-expanded={multi ? open : undefined}
        disabled={!multi}
      >
        {/* 16, like every nav icon: in the rail this sits directly under them. */}
        <span className="sb-org__tile" aria-hidden>
          <SpaceIcon kind={current.kind} />
        </span>
        <span className="sb-org__name">{current.name}</span>
        {multi && (
          <ChevronsUpDown className="sb-org__chevron" size={15} aria-hidden />
        )}
      </button>
      {open && multi && (
        <div className="sb-org__pop" ref={panelRef} role="menu">
          {spaces.map((s) => (
            <button
              key={s.spaceId}
              type="button"
              role="menuitem"
              className="sb-org__opt"
              onClick={() => {
                setSpaceId(s.spaceId)
                setOpen(false)
              }}
            >
              <span className="sb-org__tile" aria-hidden>
                {s.kind === 'personal' ? (
                  <SpaceIcon kind={s.kind} />
                ) : (
                  initialsOf(s.name)
                )}
              </span>
              <span className="sb-org__opt-name">{s.name}</span>
              {s.spaceId === current.spaceId && <Check size={15} aria-hidden />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

/**
 * Which orgs the page shows: the one being worked in, or every org linked
 * under it.
 *
 * Beside the org chooser because it qualifies the same answer — which org am
 * I working for — and drawn only while the org has orgs linked under it and
 * the page on screen can combine them. Collapsed to the rail it keeps both
 * choices as icons, stacked, each naming itself.
 */
function SidebarScope({ collapsed }: { collapsed: boolean }) {
  const { shown, scope, setScope } = useOrgScopeControl()
  if (!shown) return null
  return (
    <div className="app-sidebar__scope">
      <SegmentedControl<OrgScope>
        ariaLabel="Which orgs to show"
        size="sm"
        value={scope}
        onChange={setScope}
        options={[
          {
            value: 'org',
            icon: Building2,
            label: 'This org',
            ariaLabel: 'This org',
            tooltip: collapsed ? 'This org' : undefined,
          },
          {
            value: 'family',
            icon: Network,
            label: 'All orgs',
            ariaLabel: 'All orgs',
            tooltip: collapsed ? 'All orgs' : undefined,
          },
        ]}
      />
    </div>
  )
}

/**
 * Which kind of place this is, at a glance.
 *
 * A person's own space gets a person, not initials: initials are how one org is
 * told from another in a list of orgs, and a personal space is not one of them.
 */
function SpaceIcon({ kind }: { kind: 'org' | 'personal' }) {
  return kind === 'personal' ? <UserRound size={16} /> : <Building2 size={16} />
}

/**
 * The Schedule home: peer views on the same subject — the printed schedule, the
 * zmanim chart, and the rules that produce the times — shown as tabs in the top
 * bar. One sidebar entry, one home.
 *
 * Every printed sheet is the Schedule tab, whichever grain it is at: which one
 * is a pick in that screen's own Occasion field, not a tab of its own.
 */
const SCHEDULE_TABS = [
  { to: '/', label: 'Schedule' },
  { to: '/zmanim', label: 'Zmanim' },
  { to: '/rules', label: 'Rules' },
] as const
const SCHEDULE_PATHS: string[] = SCHEDULE_TABS.map((t) => t.to)

/**
 * Members is a section home: the households, and who holds a role. Staff are
 * members with a title, so they belong beside the roster rather than in
 * settings, where a role read as a piece of configuration.
 */
const MEMBERS_TABS = [
  {
    to: '/members',
    get label() {
      return rosterLabel()
    },
  },
  /* Somebody standing alone after a gift has no household to be listed under,
     so a shul lists them beside the households. */
  { to: '/members/donors', label: 'Donors' },
  { to: '/members/staff', label: 'Staff & roles' },
  { to: '/members/seating', label: 'Seating' },
  /* The model, where the people it describes are. Somebody asking what
     "related" means is already on Members; the assistant reads the same
     source, so the screen and the answer cannot differ. */
  { to: '/members/model', label: 'How it works' },
] as const
const MEMBERS_PATHS: string[] = MEMBERS_TABS.map((t) => t.to)

/** The Ledger's lists: what is owed, what arrived, and pledges to review. */
export const LEDGER_TABS = [
  { to: '/billing/ledger', label: 'Outstanding' },
  { to: '/billing/ledger/donations', label: 'Received' },
  { to: '/billing/ledger/pledges', label: 'Pledges' },
] as const
const LEDGER_PATHS: string[] = LEDGER_TABS.map((t) => t.to)

/** Schedules' views: what is coming, and the charges raised on a schedule. */
const BILLING_SCHEDULE_TABS = [
  { to: '/billing/schedules', label: 'Upcoming' },
  { to: '/billing/schedules/rules', label: 'Recurring charges' },
] as const
const BILLING_SCHEDULE_PATHS: string[] = BILLING_SCHEDULE_TABS.map((t) => t.to)

/** One page of a section, and the addresses beneath it that keep it marked. */
type SectionPage = { to: string; label: string; paths: string[] }
/** Pages that belong together; the sidebar draws a hairline between groups. */
type SectionGroup = { pages: SectionPage[] }

/**
 * Billing's pages as the sidebar lists them: the overview, then where money is
 * owed, raised and received, then what the books are set up with. The groups
 * carry no headings — the order clusters them and a hairline divides them. A
 * page with peer views (the Ledger's three lists, Schedules' two) carries them
 * as top-bar tabs; the sidebar names the page and stops there.
 */
const BILLING_MENU: readonly SectionGroup[] = [
  {
    pages: [
      {
        to: '/billing/overview',
        label: 'Overview',
        paths: ['/billing/overview'],
      },
    ],
  },
  {
    pages: [
      { to: '/billing/ledger', label: 'Ledger', paths: ['/billing/ledger'] },
      {
        to: '/billing/memberships',
        label: 'Memberships',
        paths: ['/billing/memberships'],
      },
      {
        to: '/billing/schedules',
        label: 'Schedules',
        paths: ['/billing/schedules'],
      },
      {
        to: '/billing/campaigns',
        label: 'Campaigns',
        paths: ['/billing/campaigns'],
      },
    ],
  },
  {
    pages: [
      { to: '/billing/items', label: 'Items', paths: ['/billing/items'] },
      {
        to: '/billing/chart-of-accounts',
        label: 'Chart of accounts',
        paths: ['/billing/chart-of-accounts'],
      },
    ],
  },
]

/** Whether an address is the page's own or beneath it. */
const within = (pathname: string, paths: readonly string[]) =>
  paths.some((p) => pathname === p || pathname.startsWith(`${p}/`))

/** The section page an address belongs to, among those offered here. */
function sectionPageFor(pathname: string): SectionPage | null {
  for (const entry of PRIMARY) {
    for (const group of entry.menu ?? []) {
      const page = here(group.pages).find((p) => within(pathname, p.paths))
      if (page) return page
    }
  }
  return null
}

/**
 * Messaging: what will go out, and what already has. Writing a message is an
 * action ("New message" in the top bar), not a place — the same composer serves
 * a send-now and a scheduled message, so it would be a strange third tab.
 * Each tab owns the paths beneath it, so a detail page keeps its tab marked.
 */
const MESSAGING_TABS = [
  { to: '/messaging/scheduled', label: 'Scheduled', nested: true },
  { to: '/messaging/sent', label: 'Sent', nested: true },
  { to: '/messaging/inbox', label: 'Inbox', nested: true },
  { to: '/messaging/lists', label: 'Lists', nested: true },
] as const
const MESSAGING_PATHS = ['/messaging']

/**
 * The type workbench's views — peers on one subject, so they ride the top bar
 * like every other set of peer views rather than sitting in a strip of their
 * own beneath the title.
 *
 * They are the platform surface's only tab set. That surface's sections are
 * otherwise flat, which is why the bar carried none until there was something
 * with views worth linking to.
 */
const TYPE_TABS = [
  { to: '/platform/type', label: 'Specimen' },
  { to: '/platform/type/glyphs', label: 'Glyph map' },
  { to: '/platform/type/compare', label: 'Compare' },
] as const
const TYPE_PATHS: string[] = TYPE_TABS.map((t) => t.to)

const DOC_TABS = [
  { to: '/documents', label: 'All documents' },
  { to: '/documents/recent', label: 'Recently edited' },
  { to: '/documents/shared', label: 'Shared by you' },
] as const
const DOC_PATHS: string[] = DOC_TABS.map((t) => t.to)

type SectionTab = { to: string; label: string; nested?: boolean }

/** The entries of a list whose sections belong to the product being served. */
const here = <T extends { to: string }>(items: readonly T[]): T[] =>
  items.filter((item) => belongsAt(item.to))

const SHARED_OUT = '/documents/shared'

/**
 * The top-bar tab set for a section home, or null for a plain single-view page.
 * A reader of a space's documents is not offered what it shares out: that
 * answer is withheld from them, and an empty list would say nothing is.
 */
export function sectionTabsFor(
  pathname: string,
  { editsDocuments }: { editsDocuments: boolean },
): readonly SectionTab[] | null {
  /* An address that does not belong here is a not-found, and names no
     section's tabs over it. */
  if (!belongsAt(pathname)) return null
  const tabs = tabSetFor(pathname)
  const offered = (tabs ? here(tabs) : []).filter(
    (tab) => editsDocuments || tab.to !== SHARED_OUT,
  )
  return offered.length > 0 ? offered : null
}

function tabSetFor(pathname: string): readonly SectionTab[] | null {
  if (TYPE_PATHS.includes(pathname)) return TYPE_TABS
  if (DOC_PATHS.includes(pathname)) return DOC_TABS
  if (SCHEDULE_PATHS.includes(pathname)) return SCHEDULE_TABS
  if (MEMBERS_PATHS.includes(pathname)) return MEMBERS_TABS
  if (LEDGER_PATHS.includes(pathname)) return LEDGER_TABS
  if (BILLING_SCHEDULE_PATHS.includes(pathname)) return BILLING_SCHEDULE_TABS
  // The composer is a page of its own — it belongs to neither tab, and showing
  // the strip with nothing selected would just look broken.
  if (pathname === '/messaging/new') return null
  if (pathname.startsWith('/messaging')) return MESSAGING_TABS
  return null
}

/**
 * The page title shown in the top bar — derived from the route so every page has
 * one without wiring. On the Schedule home the tabs stand in for the title.
 */
export function pageTitleFor(pathname: string, surface: Surface): string {
  if (surface === 'platform') {
    const seg = pathname.split('/')[2] ?? 'tasks'
    return PLATFORM_NAV.find((n) => n.section === seg)?.label ?? 'Platform'
  }
  // A surface names its pages from its OWN nav, never another surface's: a
  // path missing from the shul nav falls to that nav's default, "Schedule",
  // which is the wrong heading for a space of any other kind.
  if (surface === 'studio') {
    return (
      DOC_PAGE_TITLES[pathname] ??
      STUDIO_NAV.find((t) => pathname.startsWith(t.to))?.label ??
      'All documents'
    )
  }
  if (pathname === '/messaging/new') return 'New message'
  // Settings names the section; its own tab strip names the sub-page you are on,
  // so the title does not repeat what the marked tab already says.
  if (pathname.startsWith('/settings')) return 'Settings'
  /* A section listing its pages in the sidebar is titled by the page, which
     the marked entry beside it already places in its section. */
  const page = sectionPageFor(pathname)
  if (page) return page.label
  /* Only a section that belongs here names the page; anything else on this
     product is an address that does not exist. */
  return (
    here(PRIMARY).find(
      (t) =>
        pathname === t.to || (t.to !== '/' && pathname.startsWith(`${t.to}/`)),
    )?.label ?? (siteCarriesTimes() ? 'Schedule' : 'Not found')
  )
}

/** A primary back-office destination, and the paths it stays active across. */
type PrimaryEntry = {
  to: '/' | '/pages' | '/board' | '/members' | '/messaging' | '/billing'
  label: string
  Icon: LucideIcon
  paths: string[]
  /** The section's pages, where the sidebar lists them beneath it. */
  menu?: readonly SectionGroup[]
}

/**
 * The primary back-office destinations. Each carries the paths its sidebar
 * entry stays active across; a tabbed home owns the paths beneath its views.
 */
const PRIMARY: readonly PrimaryEntry[] = [
  {
    to: '/',
    label: 'Schedule',
    Icon: CalendarDays,
    paths: SCHEDULE_PATHS,
  },
  /*
   * Its own section rather than a view of the schedule. A page QUOTES a
   * davening time, which is an overlap and not a kind: the schedule's tabs are
   * three ways of looking at the times themselves, and a sheet for the door is
   * a different artifact that happens to name one.
   */
  {
    to: '/pages',
    label: 'Pages',
    Icon: FileText,
    /* Every path the section owns, the record page included — a nav that
       unmarks itself when you open a record says you are somewhere you are
       not. */
    paths: ['/pages', '/pages/new', '/pages/$key'],
  },
  // What the screens in the building show; its own artifact, like Pages.
  {
    to: '/board',
    label: 'Board',
    Icon: LayoutDashboard,
    paths: ['/board'],
  },
  {
    to: '/members',
    get label() {
      return rosterLabel()
    },
    Icon: Users,
    paths: MEMBERS_PATHS,
  },
  { to: '/messaging', label: 'Messaging', Icon: Send, paths: MESSAGING_PATHS },
  {
    to: '/billing',
    label: 'Billing',
    Icon: Receipt,
    paths: ['/billing'],
    menu: BILLING_MENU,
  },
]

/**
 * Studio's sections — a person's own documents, in their own space.
 *
 * Short because that is what is built, and it grows the way the org nav did —
 * by something being built, not by places being reserved for it.
 */
const STUDIO_NAV = [
  /* Books, pages, decks and boards are one place: a document is looked for by
     its name and its look, and its kind is a filter there rather than a
     destination. */
  { to: '/documents', label: 'All documents', Icon: LayoutGrid },
  /* What a document shows or plays, held once and used by any of them. */
  { to: '/media', label: 'Media', Icon: Images },
  /* The org's own furniture — a masthead, a letterhead — shown as it is
     drawn. The assistant edits it. */
  { to: '/motifs', label: 'Motifs', Icon: Shapes },
  /* The sections below are each a feature the person switches on, and are
     drawn only where they have (`belongsAt`). */
  { to: '/otzar', label: 'Otzar', Icon: Library },
  { to: '/songs', label: 'Songs', Icon: Music },
  /* A face a book is set in is as much a part of setting it as the text, and a
     Hebrew font whose points sit wrong is the commonest thing wrong with one. */
  { to: '/type', label: 'Type', Icon: Type },
] as const

/** The platform (super-admin) sections — the same collapsible sidebar, a flat set. */
export const PLATFORM_NAV = [
  { section: 'tasks', label: 'Tasks', Icon: KanbanSquare },
  /* How the platform works, and the decisions not to re-litigate. Beside the
     board because it answers the same readers about the same work. */
  { section: 'docs', label: 'Docs', Icon: FileText },
  { section: 'orgs', label: 'Orgs', Icon: Building2 },
  { section: 'people', label: 'People', Icon: Users },
  { section: 'config', label: 'App config', Icon: SlidersHorizontal },
  { section: 'integrations', label: 'Integrations', Icon: Plug },
  // Numbers about the assistant across every org — never a transcript. The
  // per-org view of its own conversations lives in that org's settings.
  { section: 'assistant', label: 'Assistant', Icon: Sparkles },
  /* The type, laid out to be compared. A design asset rather than a report on
     data, which is why it sits here: it is about the platform's own look. */
  { section: 'type', label: 'Type', Icon: Type },
] as const

/**
 * One primary section in the sidebar, and its pages beneath it where it lists
 * them.
 *
 * The pages open whenever you are in the section, and the caret folds them by
 * hand. On the icon rail they leave the column and open in a flyout beside
 * the section's icon instead.
 */
function SidebarSection({
  entry,
  pathname,
  collapsed,
  onPick,
}: {
  entry: PrimaryEntry
  pathname: string
  collapsed: boolean
  onPick?: () => void
}) {
  const inSection = within(pathname, entry.paths)
  const groups = (entry.menu ?? [])
    .map((g) => ({ ...g, pages: here(g.pages) }))
    .filter((g) => g.pages.length > 0)
  const onPage = groups.some((g) =>
    g.pages.some((p) => within(pathname, p.paths)),
  )
  const [open, setOpen] = useState(inSection)
  /* Arriving in the section opens its pages; leaving it leaves them as the
     reader set them. */
  const [wasIn, setWasIn] = useState(inSection)
  if (inSection !== wasIn) {
    setWasIn(inSection)
    if (inSection) setOpen(true)
  }
  const lists = groups.length > 0 && !collapsed
  const flies = groups.length > 0 && collapsed
  const listId = `sidebar-pages-${entry.to.replace(/\W/g, '') || 'home'}`
  const fly = useFlyout(flies)

  return (
    <>
      <div
        className="app-sidebar__row"
        ref={fly.rowRef}
        onMouseEnter={fly.show}
        onMouseLeave={fly.hideSoon}
      >
        <Link
          to={entry.to}
          className="app-sidebar__item"
          /* A section owns the paths beneath its views too, so a detail page
             keeps its entry lit. Where a page of it is marked below, the
             section is the trail to that page rather than the page itself. */
          aria-current={
            inSection ? (onPage && lists && open ? 'true' : 'page') : undefined
          }
          /* The router marks a link to any address beneath it as current;
             the section states its own mark above, so only an exact match
             may set one. */
          activeOptions={{ exact: true }}
          title={collapsed && !flies ? entry.label : undefined}
          aria-expanded={flies ? fly.open : undefined}
          aria-controls={flies && fly.open ? `${listId}-fly` : undefined}
          onClick={onPick}
          onFocus={fly.showOnFocus}
          onBlur={fly.hideOnBlur}
          onKeyDown={fly.onTriggerKey}
        >
          <entry.Icon size={17} aria-hidden />
          <span className="app-sidebar__label">{entry.label}</span>
        </Link>
        {lists ? (
          <span className="app-sidebar__fold">
            <Caret
              expanded={open}
              label={`${open ? 'Hide' : 'Show'} the pages of ${entry.label}`}
              onToggle={() => setOpen((o) => !o)}
            />
          </span>
        ) : null}
      </div>
      {flies && fly.open
        ? createPortal(
            <div
              role="navigation"
              id={`${listId}-fly`}
              ref={fly.panelRef}
              className="app-sidebar__flyout"
              aria-label={entry.label}
              style={fly.style}
              onMouseEnter={fly.show}
              onMouseLeave={fly.hideSoon}
              onBlur={fly.hideOnBlur}
              onKeyDown={fly.onPanelKey}
            >
              <Link
                to={entry.to}
                className="app-sidebar__flyout-head"
                aria-current={inSection && !onPage ? 'page' : undefined}
                activeOptions={{ exact: true }}
                onClick={fly.leave}
              >
                {entry.label}
              </Link>
              {groups.map((g) => (
                <div key={g.pages[0]?.to} className="app-sidebar__group">
                  {g.pages.map((p) => (
                    <Link
                      key={p.to}
                      to={p.to}
                      className="app-sidebar__page"
                      aria-current={
                        within(pathname, p.paths) ? 'page' : undefined
                      }
                      onClick={fly.leave}
                    >
                      {p.label}
                    </Link>
                  ))}
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
      {lists && open ? (
        <div id={listId} className="app-sidebar__pages">
          {groups.map((g) => (
            <div key={g.pages[0]?.to} className="app-sidebar__group">
              {g.pages.map((p) => (
                <Link
                  key={p.to}
                  to={p.to}
                  className="app-sidebar__page"
                  aria-current={within(pathname, p.paths) ? 'page' : undefined}
                  onClick={onPick}
                >
                  {p.label}
                </Link>
              ))}
            </div>
          ))}
        </div>
      ) : null}
    </>
  )
}

/** Persist and toggle the one collapse flag, shared by both surfaces. */
export function useSidebarCollapse() {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_KEY) === '1',
  )
  const toggle = () =>
    setCollapsed((c) => {
      localStorage.setItem(SIDEBAR_KEY, c ? '0' : '1')
      return !c
    })
  return { collapsed, toggle }
}

/**
 * The signed-in nav — one collapsible left sidebar, for both surfaces. The shul
 * surface carries the primary sections plus a Settings group (the pages that used
 * to be a second rail); the platform surface carries its flat section set.
 * Collapsing to icons reclaims the width for wide tables.
 *
 * Below the handheld edge the same nav is a drawer over the content instead of a
 * column beside it — one definition, two placements. The icon rail is the
 * *tablet* answer, not the phone one: at 375px it would take 15% of the window
 * to show icons with no labels, which is the worst wayfinding of the options.
 */
export function AppSidebar({
  surface,
  collapsed,
  drawer,
  onPick,
  account,
}: {
  surface: Surface
  collapsed: boolean
  /** Rendered as the drawer — never collapsed, and it takes the labels with it. */
  drawer?: boolean
  /**
   * A destination was chosen. The drawer closes on it.
   *
   * On each entry rather than delegated to the nav, so Enter closes it the way
   * a tap does — and so it fires for the entry you are already on, where an
   * effect keyed on the pathname would see no change and leave the drawer
   * standing over the page you just asked for.
   */
  onPick?: () => void
  /**
   * The account control, which the drawer carries on a phone.
   *
   * It sits at the trailing edge of the top bar everywhere else. At 375px that
   * bar is holding the nav button, the page's identity and its actions, and the
   * account is the one of the four nobody came to the screen for — so it moves
   * in here, beside the org chooser it already belongs with. Both answer "who am
   * I working as", which is a better home than the corner it was in.
   */
  account?: ReactNode
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  /* Read so the nav draws again when the person switches a feature on or
     off: which sections it offers depends on them. */
  useAuth()

  return (
    <nav
      /* Only the drawer answers to the toggle. The nav is rendered twice — a
         column in the flow and a drawer over it — and an id on both would make
         `aria-controls` resolve to whichever came first in the document, which
         is the copy that is not open. */
      id={drawer ? 'app-sidebar' : undefined}
      className={`app-sidebar${collapsed ? ' app-sidebar--collapsed' : ''}${
        drawer ? ' app-sidebar--drawer' : ''
      }`}
      aria-label="Sections"
    >
      <div className="app-sidebar__nav">
        {surface === 'studio' ? (
          <>
            {here(STUDIO_NAV).map((t) =>
              /* All documents leads, with the ways into where its items are
                 filed beneath it. */
              t.to === '/documents' ? (
                <DocNav key={t.to} pathname={pathname} onPick={onPick} />
              ) : (
                <Link
                  key={t.to}
                  to={t.to}
                  className="app-sidebar__item"
                  aria-current={pathname.startsWith(t.to) ? 'page' : undefined}
                  onClick={onPick}
                >
                  <t.Icon size={16} aria-hidden />
                  <span className="app-sidebar__label">{t.label}</span>
                </Link>
              ),
            )}
            <FolderTreeNav
              pathname={pathname}
              collapsed={collapsed}
              onPick={onPick}
            />
          </>
        ) : surface === 'org' ? (
          <>
            {here(PRIMARY).map((t) => (
              <SidebarSection
                key={t.to}
                entry={t}
                pathname={pathname}
                collapsed={collapsed}
                onPick={onPick}
              />
            ))}
            <Link
              to="/settings"
              className="app-sidebar__item"
              activeProps={{ 'aria-current': 'page' }}
              title={collapsed ? 'Settings' : undefined}
              onClick={onPick}
            >
              <Settings size={17} aria-hidden />
              <span className="app-sidebar__label">Settings</span>
            </Link>
          </>
        ) : (
          PLATFORM_NAV.map((t) => (
            <Link
              key={t.section}
              to="/platform/$section"
              params={{ section: t.section }}
              className="app-sidebar__item"
              activeProps={{ 'aria-current': 'page' }}
              title={collapsed ? t.label : undefined}
              onClick={onPick}
            >
              <t.Icon size={17} aria-hidden />
              <span className="app-sidebar__label">{t.label}</span>
            </Link>
          ))
        )}
      </div>
      <div className="app-sidebar__spacer" />
      {/* Org chooser (or the surface name) lives at the foot, bottom-left — and
          on a phone the account joins it there. Both answer the same question. */}
      {account ? <div className="app-sidebar__account">{account}</div> : null}
      {surface === 'org' ? <SidebarScope collapsed={collapsed} /> : null}
      <div className="app-sidebar__foot">
        {/* The chooser is how you leave a space, so every tenant surface carries
            it — a personal space without one is a room with no door. Platform
            is not a tenant and names itself instead. */}
        {surface === 'platform' ? (
          <div className="app-sidebar__surface app-sidebar__label">
            Platform admin
          </div>
        ) : (
          <SidebarOrg />
        )}
      </div>
    </nav>
  )
}
